import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardMedia, CardContent, CardActions,
  Chip, IconButton, Grid, TextField, InputAdornment, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  ToggleButton, ToggleButtonGroup, Tab, Tabs, Avatar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Snackbar, Alert,
} from '@mui/material';
import {
  Add, Search, Edit, Delete, FilterList, Storefront,
  VisibilityOff, Visibility, Inbox, Inventory2,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { useAuth } from '../contexts/AuthContext';
import MerchandiseFormDialog from '../components/MerchandiseFormDialog';
import MerchandiseOrdersTab from '../components/MerchandiseOrdersTab';
import MerchandiseRevenueTab from '../components/MerchandiseRevenueTab';
import MerchandiseStockTab from '../components/MerchandiseStockTab';
import AdjustStockDialog from '../components/AdjustStockDialog';
import StudioSelector, { StudioOption } from '../components/StudioSelector';

interface MerchandiseVariant {
  label: string;
  sku: string;
  priceOverride: number | null;
  stockQuantity: number;
  isActive: boolean;
}

interface MerchandiseItem {
  _id: string;
  title: string;
  description?: string;
  images: string[];
  price: number;
  category?: string;
  tags?: string[];
  isActive: boolean;
  sku?: string;
  stockQuantity?: number;
  variants?: MerchandiseVariant[];
  checkoutEnabled?: boolean;
  payAtStudio?: boolean;
  gstRate?: number;
  hsnCode?: string;
  studio?: { _id: string; studioName: string };
  createdAt: string;
}

/**
 * Total sellable units for an item, hiding the simple-vs-variant split.
 *
 * A variant product's stock is the sum of its ACTIVE variants — a discontinued
 * size still holding 4 units is not stock anyone can buy, so counting it would
 * overstate what is on the shelf.
 */
const stockOf = (item: MerchandiseItem): number =>
  item.variants?.length
    ? item.variants.filter(v => v.isActive !== false).reduce((s, v) => s + (v.stockQuantity ?? 0), 0)
    : item.stockQuantity ?? 0;

interface Enquiry {
  _id: string;
  merchandise: { _id: string; title: string; images: string[]; price: number };
  user: { name: string; phoneNumber?: string; email?: string };
  createdAt: string;
}

export default function MerchandisePage() {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<MerchandiseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [tagFilter, setTagFilter] = useState('');
  const [studios, setStudios] = useState<{ _id: string; studioName: string }[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<MerchandiseItem | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [enquirySearch, setEnquirySearch] = useState('');
  const [enquiryProductFilter, setEnquiryProductFilter] = useState('');

  /**
   * The studio this page is scoped to.
   *
   * For studio roles StudioSelector seeds this from auth and renders nothing —
   * the server pins them to their own studio regardless. For super-admins it is
   * the actual filter: previously this page sent NO studioId and no pageSize, so
   * `/admin/all` returned the newest 20 items across every studio and all the
   * filtering below ran over that truncated set. That is why the page looked
   * like it was hardcoded to Purple Pilates — PP simply filled the window.
   */
  const [selectedStudio, setSelectedStudio] = useState<StudioOption | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [stockItem, setStockItem] = useState<MerchandiseItem | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getMerchandise({
        ...(selectedStudio ? { studioId: selectedStudio._id } : {}),
        pageSize: 100,
      });
      setItems(res?.merchandise || []);
    } catch (err: any) {
      console.error(err);
      setToast({ msg: err?.response?.data?.message || 'Failed to load items', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedStudio]);

  const fetchStudios = useCallback(async () => {
    if (isStudio) return;
    try {
      const res = await apiService.getStudios({ pageSize: 200 });
      setStudios((res as any)?.data?.studios || (res as any)?.studios || (res as any)?.data || []);
    } catch {}
  }, [isStudio]);

  const fetchEnquiries = useCallback(async () => {
    // The enquiries endpoint is studio-scoped and 400s without a studioId.
    // Super-admins must pick a studio first — previously the page sent nothing,
    // every super-admin request failed, and the error was swallowed into an
    // empty "No enquiries yet" state that looked like real data.
    if (!isStudio && !selectedStudio) {
      setEnquiries([]);
      return;
    }
    setEnquiriesLoading(true);
    try {
      const res = await apiService.getMerchandiseEnquiries(
        selectedStudio ? { studioId: selectedStudio._id } : {},
      );
      setEnquiries(res?.enquiries || []);
    } catch (err: any) {
      console.error(err);
      setToast({ msg: err?.response?.data?.message || 'Failed to load enquiries', severity: 'error' });
    } finally {
      setEnquiriesLoading(false);
    }
  }, [isStudio, selectedStudio]);

  useEffect(() => {
    fetchItems();
    fetchStudios();
  }, [fetchItems, fetchStudios]);

  useEffect(() => {
    if (tab === 1) fetchEnquiries();
  }, [tab, fetchEnquiries]);

  const allTags = Array.from(new Set(items.flatMap(i => i.tags || [])));

  const filtered = items.filter(item => {
    const matchSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? item.isActive : !item.isActive);
    const matchTag = !tagFilter || item.tags?.includes(tagFilter);
    return matchSearch && matchStatus && matchTag;
  });

  // Every handler below used to swallow its error (`catch { }` or a bare
  // console.error), so a failed save closed the dialog and looked exactly like a
  // successful one. The server has real, specific messages — surface them.
  const errMsg = (err: any, fallback: string) =>
    err?.response?.data?.message || err?.message || fallback;

  const handleSave = async (data: any, pinStudioId?: string) => {
    setSaving(true);
    try {
      // pinStudioId (studio roles only): the item's location, or the one
      // chosen in the dialog under "All locations" — the switcher's __all__
      // is refused for writes.
      if (editItem) {
        await apiService.updateMerchandise(data, pinStudioId);
      } else {
        const studioId = isStudio ? pinStudioId || '' : data.studioId;
        await apiService.createMerchandise(studioId, data);
      }
      setFormOpen(false);
      setEditItem(null);
      setToast({ msg: editItem ? 'Item updated' : 'Item created', severity: 'success' });
      fetchItems();
    } catch (err: any) {
      console.error(err);
      setToast({ msg: errMsg(err, 'Failed to save item'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: MerchandiseItem) => {
    try {
      await apiService.updateMerchandise({ merchandiseId: item._id, isActive: !item.isActive }, studioIdOf((item as any).studio));
      setToast({ msg: item.isActive ? 'Item hidden from the app' : 'Item is now live', severity: 'success' });
      fetchItems();
    } catch (err: any) {
      setToast({ msg: errMsg(err, 'Failed to update item'), severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteMerchandise(
        deleteId,
        studioIdOf((items.find((i) => i._id === deleteId) as any)?.studio),
      );
      setDeleteId(null);
      setToast({ msg: 'Item deleted', severity: 'success' });
      fetchItems();
    } catch (err: any) {
      // The server refuses to delete a product that has been ordered (its stock
      // ledger would lose its subject) and says so — that message is the whole
      // point, so close the dialog and show it.
      setDeleteId(null);
      setToast({ msg: errMsg(err, 'Failed to delete item'), severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const uniqueProducts = Array.from(new Map(enquiries.map(e => [e.merchandise._id, e.merchandise])).values());
  const filteredEnquiries = enquiries.filter(e => {
    const matchSearch = !enquirySearch ||
      e.user.name?.toLowerCase().includes(enquirySearch.toLowerCase()) ||
      e.user.phoneNumber?.includes(enquirySearch) ||
      e.merchandise.title.toLowerCase().includes(enquirySearch.toLowerCase());
    const matchProduct = !enquiryProductFilter || e.merchandise._id === enquiryProductFilter;
    return matchSearch && matchProduct;
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Storefront sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Merchandise</Typography>
            <Typography variant="caption" color="text.secondary">
              {tab === 0 && `${filtered.length} item${filtered.length !== 1 ? 's' : ''}`}
              {tab === 2 && `${filteredEnquiries.length} enquir${filteredEnquiries.length !== 1 ? 'ies' : 'y'}`}
              {tab === 1 && 'Fulfilment — collect at studio'}
              {tab === 3 && 'Tracked separately from payouts'}
              {tab === 4 && 'Live stock across every variant'}
            </Typography>
          </Box>
        </Box>
        {tab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditItem(null); setFormOpen(true); }}>
            Add Item
          </Button>
        )}
      </Box>

      {/* Tabs. Order = the studio's daily workflow: stock the shelf, fulfil what
          sold, chase enquiries, then look at the money. */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Items" />
        <Tab label="Orders" />
        <Tab label={`Enquiries${enquiries.length > 0 ? ` (${enquiries.length})` : ''}`} />
        <Tab label="Revenue" />
        <Tab label="Stock" />
      </Tabs>

      {/* ── Tab 1: Orders ── */}
      {tab === 1 && (
        <MerchandiseOrdersTab
          selectedStudio={selectedStudio}
          onStudioChange={setSelectedStudio}
          onToast={(msg, severity) => setToast({ msg, severity })}
        />
      )}

      {/* ── Tab 3: Revenue ── */}
      {tab === 3 && (
        <MerchandiseRevenueTab
          selectedStudio={selectedStudio}
          onStudioChange={setSelectedStudio}
          onToast={(msg, severity) => setToast({ msg, severity })}
        />
      )}

      {/* ── Tab 4: Stock ── */}
      {tab === 4 && (
        <MerchandiseStockTab
          selectedStudio={selectedStudio}
          onStudioChange={setSelectedStudio}
          onToast={(msg, severity) => setToast({ msg, severity })}
        />
      )}

      {/* ── Tab 2: Enquiries ── */}
      {tab === 2 && (
        <Box>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
            {/* Required here: the endpoint is studio-scoped and 400s without it. */}
            <StudioSelector value={selectedStudio} onChange={setSelectedStudio} required />
            <TextField size="small" placeholder="Search by name, phone, or product..."
              value={enquirySearch} onChange={e => setEnquirySearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 280 }}
            />
            {uniqueProducts.length > 1 && (
              <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                <FilterList fontSize="small" color="action" />
                <Chip label="All Products" size="small" clickable
                  color={!enquiryProductFilter ? 'primary' : 'default'}
                  onClick={() => setEnquiryProductFilter('')}
                />
                {uniqueProducts.map(p => (
                  <Chip key={p._id} label={p.title} size="small" clickable
                    color={enquiryProductFilter === p._id ? 'primary' : 'default'}
                    onClick={() => setEnquiryProductFilter(enquiryProductFilter === p._id ? '' : p._id)}
                  />
                ))}
              </Box>
            )}
          </Box>

          {enquiriesLoading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : !isStudio && !selectedStudio ? (
            // Be explicit rather than showing "No enquiries yet", which reads as
            // "this studio has none" when it actually means "you haven't chosen one".
            <Box textAlign="center" py={10}>
              <Storefront sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">Select a studio to view its enquiries</Typography>
            </Box>
          ) : filteredEnquiries.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Inbox sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {enquiries.length === 0 ? 'No enquiries yet' : 'No enquiries match your filters'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><Typography variant="caption" fontWeight={700}>Product</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={700}>Customer</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={700}>Phone</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={700}>Email</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={700}>Enquired At</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEnquiries.map(e => (
                    <TableRow key={e._id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            src={e.merchandise.images?.[0]}
                            variant="rounded"
                            sx={{ width: 36, height: 36, bgcolor: 'grey.100' }}
                          >
                            <Storefront fontSize="small" sx={{ color: 'grey.400' }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 180 }} noWrap>
                              {e.merchandise.title}
                            </Typography>
                            {e.merchandise.price > 0 && (
                              <Typography variant="caption" color="text.secondary">₹{e.merchandise.price}</Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{e.user.name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{e.user.phoneNumber || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{e.user.email || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(e.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Tab 0: Items ── */}
      {tab === 0 && <>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        {/* Renders only for super-admins; studio roles are pinned by the server. */}
        <StudioSelector value={selectedStudio} onChange={setSelectedStudio} />
        <TextField size="small" placeholder="Search items..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
        {allTags.length > 0 && (
          <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
            <FilterList fontSize="small" color="action" />
            {allTags.map(tag => (
              <Chip key={tag} label={tag} size="small" clickable
                color={tagFilter === tag ? 'primary' : 'default'}
                onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Storefront sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            {search || tagFilter || statusFilter !== 'all' ? 'No items match your filters' : 'No merchandise yet'}
          </Typography>
          <Button sx={{ mt: 2 }} variant="outlined" startIcon={<Add />}
            onClick={() => { setEditItem(null); setFormOpen(true); }}>
            Add First Item
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(item => (
            <Grid key={item._id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                opacity: item.isActive ? 1 : 0.65,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 4 },
              }}>
                {/* Image */}
                <Box sx={{ position: 'relative' }}>
                  {item.images?.[0] ? (
                    <CardMedia component="img" height={180} image={item.images[0]}
                      alt={item.title} sx={{ objectFit: 'cover' }} />
                  ) : (
                    <Box height={180} display="flex" alignItems="center" justifyContent="center"
                      bgcolor="grey.100">
                      <Storefront sx={{ fontSize: 40, color: 'grey.400' }} />
                    </Box>
                  )}
                  {!item.isActive && (
                    <Chip label="Inactive" size="small" color="default"
                      sx={{ position: 'absolute', top: 8, left: 8, fontSize: 10 }} />
                  )}
                  {item.images.length > 1 && (
                    <Chip label={`+${item.images.length - 1}`} size="small"
                      sx={{ position: 'absolute', top: 8, right: 8, fontSize: 10 }} />
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>{item.title}</Typography>
                  {item.price > 0 && (
                    <Typography variant="h6" color="primary.main" fontWeight={800} sx={{ my: 0.5 }}>
                      ₹{item.price}
                    </Typography>
                  )}
                  {item.description && (
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description}
                    </Typography>
                  )}
                  {!isStudio && item.studio && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {item.studio.studioName}
                    </Typography>
                  )}
                  {item.tags && item.tags.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                      {item.tags.map(t => <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 10 }} />)}
                    </Box>
                  )}

                  {/* Stock + selling mode — the two things a studio actually needs
                      at a glance, and neither was shown before. */}
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                    {(() => {
                      const total = stockOf(item);
                      const out = total === 0;
                      const low = !out && total <= 5;
                      return (
                        <Chip
                          size="small"
                          label={
                            out ? 'Out of stock'
                              : `${total} in stock${item.variants?.length ? ` · ${item.variants.length} options` : ''}`
                          }
                          color={out ? 'error' : low ? 'warning' : 'default'}
                          variant={out || low ? 'filled' : 'outlined'}
                          sx={{ fontSize: 10 }}
                        />
                      );
                    })()}
                    {!item.checkoutEnabled && (
                      <Tooltip title="Browse-only — the app shows 'Enquire at Studio' instead of Add to Bag">
                        <Chip label="Enquiry only" size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      </Tooltip>
                    )}
                    {item.payAtStudio && (
                      <Chip label="Pay at studio" size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ pt: 0, justifyContent: 'flex-end', gap: 0.5 }}>
                  <Tooltip title="Adjust stock">
                    <IconButton size="small" onClick={() => setStockItem(item)}>
                      <Inventory2 fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={item.isActive ? 'Hide' : 'Show'}>
                    <IconButton size="small" onClick={() => handleToggleActive(item)}>
                      {item.isActive ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setEditItem(item); setFormOpen(true); }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteId(item._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      </>}

      {/* Form Dialog */}
      <MerchandiseFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditItem(null); }}
        onSave={handleSave}
        initialData={editItem}
        studios={studios}
        loading={saving}
        defaultStudioId={selectedStudio?._id}
      />

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Item?</DialogTitle>
        <DialogContent>
          <Typography>
            This permanently removes the item. Products that have already been
            ordered cannot be deleted — mark them inactive instead to hide them
            from the app while keeping their order and stock history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleteLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock — the only sanctioned path for stock to change outside an order. */}
      <AdjustStockDialog
        open={!!stockItem}
        item={stockItem}
        studioId={selectedStudio?._id}
        onClose={() => setStockItem(null)}
        onDone={(msg, severity) => {
          setToast({ msg, severity });
          if (severity === 'success') fetchItems();
        }}
      />

      {/* Feedback. Previously every failure was silent — a failed save closed the
          dialog and was indistinguishable from a successful one. */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
